import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { emailAdmin } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusStyle } from "@/lib/status";
import { CalendarDays, ClipboardCheck, Receipt, Briefcase, UserX, ShieldAlert, Flag, CalendarOff, Loader2, ListChecks, Radio, MapPinOff, LogIn, CheckCircle2, AlertTriangle, Hourglass, LogOut, Timer, Mail, MailWarning, Link2, ChevronDown, ChevronRight, CalendarCheck } from "lucide-react";
import { tint } from "@/lib/colour";

const CARDS = [
  { key: "upcoming_week", label: "Shifts this week", icon: CalendarDays, to: "/roster", color: "var(--brand)" },
  { key: "unassigned_upcoming", label: "Unassigned (7 days)", icon: UserX, to: "/roster", color: "#D97706" },
  { key: "pending_leave", label: "Unapproved leave", icon: CalendarOff, to: "/leave?status=Pending", color: "#DC2626" },
  { key: "pending_timesheets", label: "Timesheets to review", icon: ClipboardCheck, to: "/timesheets", color: "#3f9963" },
  { key: "flagged_timesheets", label: "Flagged for review", icon: Flag, to: "/timesheets", color: "#EF4444" },
  { key: "awaiting_invoicing", label: "Awaiting Invoicing", icon: Receipt, to: "/invoicing", color: "#00c86e" },
  { key: "job_board_open", label: "Open on job board", icon: Briefcase, to: "/job-board", color: "#7f6a59" },
  { key: "overrides_active", label: "Active overrides", icon: ShieldAlert, to: "/roster", color: "#991B1B" },
];

export default function Dashboard() {
  const { user, can } = useAuth();
  const [data, setData] = useState(null);
  const [incMeta, setIncMeta] = useState(null);
  const seeIncidents = can.has("incidents.view");

  useEffect(() => {
    const refresh = () => api.dashboard().then(setData).catch(() => {});
    refresh();
    if (seeIncidents) api.incidentsMeta().then(setIncMeta).catch(() => {});
    const id = setInterval(refresh, 60000); // live auto-refresh every minute
    return () => clearInterval(id);
  }, [seeIncidents]);

  if (!data) return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const total = Object.values(data.by_status || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="p-5">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-brand">Good day, {user?.name?.split(" ")[0]}</h1>
        <p className="text-xs text-slate-500 mt-0.5">Your operational snapshot · roster activity over the last 30 days.</p>
      </div>

      {seeIncidents && incMeta && incMeta.open > 0 && (
        <Link to="/incidents?status=open" data-testid="incident-alert-widget"
          className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 hover:bg-red-100/60 transition-colors">
          <div className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="h-5 w-5" />
            <span className="text-sm font-semibold">{incMeta.open} open incident{incMeta.open > 1 ? "s" : ""} to manage</span>
            {incMeta.high_open > 0 && <span className="rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5">{incMeta.high_open} High/Critical</span>}
          </div>
          <span className="text-xs font-semibold text-red-700">{incMeta.submitted} new · {incMeta.under_review} under review →</span>
        </Link>
      )}

      {seeIncidents && incMeta && incMeta.outstanding_actions > 0 && (
        <Link to="/incidents?status=open_actions" data-testid="incident-actions-widget"
          className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 hover:bg-amber-100/60 transition-colors">
          <div className="flex items-center gap-2 text-amber-800">
            <ListChecks className="h-5 w-5" />
            <span className="text-sm font-semibold">{incMeta.outstanding_actions} outstanding corrective action{incMeta.outstanding_actions > 1 ? "s" : ""}</span>
            {incMeta.overdue_actions > 0 && <span className="rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5">{incMeta.overdue_actions} overdue</span>}
          </div>
          <span className="text-xs font-semibold text-amber-800">across {incMeta.action_incidents} incident{incMeta.action_incidents > 1 ? "s" : ""} →</span>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
        {CARDS.map((c) => (
          <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`}
            className="bg-white border border-slate-200 rounded-xl p-3 hover:shadow-md hover:-translate-y-0.5 transition-all group">
            <div className="flex items-center justify-between">
              <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tint(c.color, "1A") }}>
                <c.icon className="h-4 w-4" style={{ color: c.color }} />
              </span>
              <span className="text-2xl font-bold text-slate-900 tabular-nums font-brand">{data[c.key] ?? 0}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-2 leading-tight group-hover:text-slate-700">{c.label}</div>
          </Link>
        ))}
      </div>

      <CheckInsWidget />

      <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-2xl">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Shift status breakdown</span>
          <span className="text-[10px] text-slate-400">Last 14 days → next 30 days</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden mb-4">
          {Object.entries(data.by_status || {}).map(([s, n]) => (
            <div key={s} style={{ width: `${(n / total) * 100}%`, backgroundColor: statusStyle(s).dot }} title={`${s}: ${n}`} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {Object.entries(data.by_status || {}).map(([s, n]) => (
            <div key={s} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/70 px-2.5 py-1.5">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: statusStyle(s).dot }} />
              <span className="text-xs text-slate-600 truncate">{s}</span>
              <span className="text-sm font-bold text-slate-900 ml-auto tabular-nums font-brand">{n}</span>
            </div>
          ))}
        </div>
      </div>

      <ActiveNow data={data} />

      {can.has("settings.reminders", "system.audit_log") && <EmailHealth />}
    </div>
  );
}

// Participant check-ins that are overdue or due in the next 14 days (office staff).
function CheckInsWidget() {
  const [rows, setRows] = useState(null);
  const [mine, setMine] = useState(false);
  useEffect(() => {
    const load = () => api.openCheckIns(14).then(setRows).catch(() => setRows([]));
    load();
    const id = setInterval(load, 300000);
    return () => clearInterval(id);
  }, []);
  if (!rows || rows.length === 0) return null;
  const isMine = (r) => !!r.mine;
  const shown = mine ? rows.filter(isMine) : rows;
  const overdue = rows.filter((r) => r.overdue).length;
  const fmt = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-2xl mb-4" data-testid="checkins-widget">
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <CalendarCheck className="h-3.5 w-3.5" />Participant check-ins
          {overdue > 0 && <span className="rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 normal-case tracking-normal" data-testid="checkins-overdue-count">{overdue} overdue</span>}
        </span>
        <label className="text-[10px] text-slate-500 flex items-center gap-1 cursor-pointer">
          <input type="checkbox" className="accent-brand" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="checkins-mine" />Assigned to me
        </label>
      </div>
      {shown.length === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">Nothing assigned to you.</div>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="checkins-widget-list">
          {shown.slice(0, 12).map((r) => (
            <Link key={r.id} to={`/participants?open=${encodeURIComponent(r.participant)}&tab=compliance`} className="flex items-center gap-3 py-1.5 hover:bg-slate-50 rounded px-1" data-testid={`checkins-row-${r.id}`}>
              <span className={`text-xs font-semibold w-24 shrink-0 ${r.overdue ? "text-red-600" : "text-slate-600"}`}>{fmt(r.due_date)}</span>
              <span className="text-sm text-slate-800 truncate flex-1">{r.participant_name}{r.purpose ? <span className="text-slate-400"> · {r.purpose}</span> : null}</span>
              <span className={`text-[11px] shrink-0 ${isMine(r) ? "font-semibold text-slate-800" : "text-slate-400"}`}>{r.assignee_name || "Unassigned"}</span>
            </Link>
          ))}
          {shown.length > 12 && <div className="text-[10px] text-slate-400 pt-2">+{shown.length - 12} more</div>}
        </div>
      )}
    </div>
  );
}

function EmailHealth() {
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = () => emailAdmin.health(7).then(setH).catch(() => {});
    load();
    const id = setInterval(load, 300000); // refresh every 5 min
    return () => clearInterval(id);
  }, []);
  if (!h) return null;
  const hasFail = (h.failed || 0) > 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-2xl mt-4" data-testid="email-health-widget">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          {hasFail ? <MailWarning className="h-3.5 w-3.5 text-red-500" /> : <Mail className="h-3.5 w-3.5 text-slate-400" />}
          Reminder emails · last 7 days
        </span>
        <Link to="/settings/reminders" className="text-[10px] font-semibold text-slate-500 hover:text-slate-800" data-testid="email-health-manage">Manage →</Link>
      </div>

      {h.total === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg" data-testid="email-health-empty">
          No automated emails sent in the last 7 days.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2" data-testid="email-health-sent">
            <div className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /><span className="text-[11px] font-semibold">Delivered</span></div>
            <div className="text-2xl font-bold text-slate-900 tabular-nums font-brand mt-0.5">{h.sent}</div>
          </div>
          <div className={`rounded-lg border px-3 py-2 ${hasFail ? "border-red-200 bg-red-50/80" : "border-slate-100 bg-slate-50/70"}`} data-testid="email-health-failed">
            <div className={`flex items-center gap-1.5 ${hasFail ? "text-red-700" : "text-slate-500"}`}><AlertTriangle className="h-3.5 w-3.5" /><span className="text-[11px] font-semibold">Failed</span></div>
            <div className={`text-2xl font-bold tabular-nums font-brand mt-0.5 ${hasFail ? "text-red-700" : "text-slate-900"}`}>{h.failed}</div>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2" data-testid="email-health-skipped">
            <div className="flex items-center gap-1.5 text-slate-500"><LogOut className="h-3.5 w-3.5" /><span className="text-[11px] font-semibold">Skipped</span></div>
            <div className="text-2xl font-bold text-slate-900 tabular-nums font-brand mt-0.5">{h.skipped}</div>
          </div>
        </div>
      )}

      {hasFail && (h.recent_failures || []).length > 0 && (
        <div className="mt-3 space-y-1" data-testid="email-health-failures">
          {h.recent_failures.map((f, i) => (
            <div key={i} className="text-[11px] text-slate-600 border-l-2 border-red-200 pl-2">
              <span className="font-semibold text-slate-800">{f.label}</span>
              {f.to ? <span className="text-slate-400"> · {f.to}</span> : null}
              {f.detail ? <div className="text-red-500 truncate">{f.detail}</div> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CLOCK_META = {
  on_site: { label: "On site", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  not_clocked_in: { label: "Not clocked in", icon: LogIn, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  clocked_out: { label: "Clocked out", icon: LogOut, cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

// Primary status badge for a live shift row.
function statusBadge(a) {
  if (a.phase === "upcoming")
    return { label: "Starts soon", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: Hourglass };
  if (a.late_start)
    return { label: `Late ${a.minutes_late}m`, cls: "bg-red-100 text-red-700 border-red-200", Icon: AlertTriangle };
  if (a.phase === "overtime")
    return { label: "Over shift time", cls: "bg-red-100 text-red-700 border-red-200", Icon: AlertTriangle };
  const cm = CLOCK_META[a.clock_state] || CLOCK_META.not_clocked_in;
  return { label: cm.label, cls: cm.cls, Icon: cm.icon };
}

function rightMeta(a) {
  if (a.phase === "upcoming") return `in ${a.minutes_until_start}m`;
  if (a.phase === "overtime") return `${a.minutes_over}m over`;
  if (a.phase === "wrapped") return "clocked out";
  return `${a.minutes_remaining} min left`;
}

function Pill({ show, count, one, many, cls, Icon, testid }) {
  if (!show) return null;
  return (
    <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-1 flex items-center gap-1 ${cls}`} data-testid={testid}>
      <Icon className="h-3 w-3" />{count} {count === 1 ? one : many}
    </span>
  );
}

const LEG_STATE = {
  done: { label: "Done", cls: "text-slate-400" },
  current: { label: "Now", cls: "text-emerald-700 font-semibold" },
  upcoming: { label: "Next", cls: "text-slate-500" },
};

function ActiveNow({ data }) {
  const rows = data.active_now || [];
  const [expanded, setExpanded] = useState({});
  const toggle = (name) => setExpanded((e) => ({ ...e, [name]: !e[name] }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-2xl mt-4" data-testid="active-now-report">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          Shifts happening now
        </span>
        <span className="text-[10px] text-slate-400 flex items-center gap-1"><Radio className="h-3 w-3" /> Live · refreshes each minute</span>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-bold text-slate-900 tabular-nums font-brand" data-testid="active-now-count">{data.active_now_in_progress || 0}</span>
          <span className="text-xs text-slate-500">in progress</span>
        </div>
        <Pill show={(data.active_now_late_start || 0) > 0} count={data.active_now_late_start} one="late start" many="late starts"
          cls="text-red-700 bg-red-50 border-red-200" Icon={AlertTriangle} testid="active-now-late" />
        <Pill show={(data.active_now_late_clock_in || 0) > 0} count={data.active_now_late_clock_in} one="clocked in late" many="clocked in late"
          cls="text-red-700 bg-red-50 border-red-200" Icon={Timer} testid="active-now-late-clockin" />
        <Pill show={(data.active_now_overtime || 0) > 0} count={data.active_now_overtime} one="over shift time" many="over shift time"
          cls="text-red-700 bg-red-50 border-red-200" Icon={AlertTriangle} testid="active-now-overtime" />
        <Pill show={(data.active_now_not_clocked_in || 0) > 0} count={data.active_now_not_clocked_in} one="not clocked in" many="not clocked in"
          cls="text-amber-700 bg-amber-50 border-amber-200" Icon={LogIn} testid="active-now-attention" />
        <Pill show={(data.active_now_starts_soon || 0) > 0} count={data.active_now_starts_soon} one="starts soon" many="start soon"
          cls="text-indigo-700 bg-indigo-50 border-indigo-200" Icon={Hourglass} testid="active-now-startssoon" />
        <Pill show={(data.active_now_wrapped || 0) > 0} count={data.active_now_wrapped} one="just clocked out" many="just clocked out"
          cls="text-slate-500 bg-slate-100 border-slate-200" Icon={LogOut} testid="active-now-wrapped" />
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg" data-testid="active-now-empty">
          No shifts running or due to start soon.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="active-now-list">
          {rows.map((a) => {
            const badge = statusBadge(a);
            const BIcon = badge.Icon;
            const alert = a.late_start || a.overtime || a.late_clock_in;
            const rowCls = alert ? "border-red-200 bg-red-50/70 hover:bg-red-100/60"
              : a.phase === "upcoming" ? "border-indigo-100 bg-indigo-50/50 hover:bg-indigo-100/50"
              : a.phase === "wrapped" ? "border-slate-100 bg-slate-50/40 hover:bg-slate-100/60 opacity-80"
              : "border-slate-100 bg-slate-50/60 hover:bg-slate-100/70";
            const linked = (a.legs || []).length > 1;
            const curLeg = linked ? (a.legs.find((l) => l.state === "current") || a.legs[a.legs.length - 1]) : null;
            const open = !!expanded[a.name];
            const rowInner = (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{a.participant_name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {a.unassigned ? <span className="text-rose-600 font-semibold">Unassigned</span> : a.support_worker_name}
                    {a.location ? ` · ${a.location}` : ""}
                  </div>
                </div>
                {a.out_of_range && (
                  <MapPinOff className="h-3.5 w-3.5 text-orange-500 shrink-0" title="Clock GPS out of range" />
                )}
                {a.late_clock_in && !a.late_start && (
                  <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0 bg-red-100 text-red-700 border-red-200" data-testid={`active-now-lateclockin-badge-${a.name}`}>
                    <Timer className="h-3 w-3" />Clocked in {a.minutes_late_clock_in}m late
                  </span>
                )}
                <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0 ${badge.cls}`} data-testid={`active-now-status-badge-${a.name}`}>
                  <BIcon className="h-3 w-3" />{badge.label}
                </span>
                <div className="text-right shrink-0 w-20">
                  <div className="text-xs font-semibold text-slate-700 tabular-nums">{a.start_time}–{a.end_time}</div>
                  <div className="text-[10px] text-slate-400 tabular-nums">{rightMeta(a)}</div>
                </div>
                {linked && (
                  <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0 bg-violet-50 text-violet-700 border-violet-200" data-testid={`active-now-linked-badge-${a.name}`}>
                    <Link2 className="h-3 w-3" />Linked · leg {curLeg.leg}/{a.legs.length}
                    {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </span>
                )}
              </>
            );
            if (!linked) {
              return (
                <Link key={a.row_key || a.name} to={`/roster?shift=${encodeURIComponent(a.name)}`} data-testid={`active-now-row-${a.row_key || a.name}`}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${rowCls}`}>
                  {rowInner}
                </Link>
              );
            }
            return (
              <div key={a.name} data-testid={`active-now-row-${a.name}`}>
                <button type="button" onClick={() => toggle(a.name)} aria-expanded={open}
                  className={`w-full text-left flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${rowCls}`}>
                  {rowInner}
                </button>
                {open && (
                  <div className="mt-1 ml-4 space-y-1" data-testid={`active-now-legs-${a.name}`}>
                    {a.legs.map((l) => {
                      const ls = LEG_STATE[l.state] || LEG_STATE.upcoming;
                      return (
                        <Link key={l.name} to={`/roster?shift=${encodeURIComponent(l.name)}`} data-testid={`active-now-leg-${l.name}`}
                          className={`flex items-center gap-3 rounded-md border px-3 py-1.5 text-xs bg-white hover:bg-slate-50 ${l.state === "current" ? "border-emerald-300" : "border-slate-200"}`}>
                          <span className="text-[10px] font-semibold text-violet-600 w-10">Leg {l.leg}</span>
                          <span className="flex-1 min-w-0 truncate text-slate-700">{l.participant_name}</span>
                          <span className="tabular-nums text-slate-600">{l.start_time}–{l.end_time}</span>
                          <span className={`w-10 text-right ${ls.cls}`}>{ls.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
